// ProjectBlockedPage — reached when a project the user tried to enter is
// unreachable (suspended / terminated / expired-trial), or when the resolver
// finds the user has memberships but none reachable. The bar (SlimAuthedLayout,
// brand=switcher) names the project via `?project=<id>`, coherent with an
// in-project page; the switcher there is the escape hatch.
//
// Reasons (from `?reason=` query param):
//   - `suspended`     — apiClient 403 PROJECT_SUSPENDED, or resolver single-blocked
//   - `terminated`    — apiClient 403 PROJECT_TERMINATED, or resolver single-blocked
//   - `trial-expired` — apiClient 403 TRIAL_EXPIRED, or resolver single-blocked
//   - `all-blocked`   — resolver: >1 membership, none reachable.
//
// "Contact us" (placeholder toast) shows on every blocked state for MEMBERS —
// the customer-facing CTA (reactivate / renew). Hidden for a sysadmin, who
// manages the project in the platform portal.
//
// Footer is ROLE-AWARE + state-aware so it never dead-ends / loops:
//   - sysadmin                         → "Go to platform" (/platform/projects)
//   - member with ≥1 reachable project → "Choose a project" (/select-project)
//   - member with none reachable       → "Sign out"
// (0-reachable members are NOT sent to /select-project — its picker would show
// only their blocked cards, a soft loop; sign out is the honest exit. The avatar
// also carries Sign out on every variant, so there is always a way out.)

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '../components/ui/Alert.js';
import { Link } from '../components/ui/Link.js';
import { Button } from '../components/ui/Button.js';
import { useToast } from '../components/ui/Toast.js';
import { useMe } from '../hooks/useMe.js';
import { useLogout } from '../hooks/useLogout.js';
import { isReachable } from '../lib/projectAccess.js';

type Reason = 'suspended' | 'terminated' | 'all-blocked' | 'trial-expired';

function normalizeReason(raw: string | null): Reason {
  if (
    raw === 'suspended' ||
    raw === 'terminated' ||
    raw === 'all-blocked' ||
    raw === 'trial-expired'
  )
    return raw;
  return 'all-blocked';
}

export function ProjectBlockedPage() {
  const { t } = useTranslation(['common', 'nav']);
  const [params] = useSearchParams();
  const reason = normalizeReason(params.get('reason'));
  const { data: me } = useMe();
  const logout = useLogout();
  const toast = useToast();
  const [now] = useState(() => Date.now());

  // 'all-blocked' / 'trial-expired' are info-tone; suspended/terminated warn.
  const variant = reason === 'all-blocked' || reason === 'trial-expired' ? 'info' : 'warning';

  const isSysadmin = me?.user.is_sysadmin ?? false;
  const hasReachable = (me?.availableProjects ?? []).some((p) => isReachable(p, now));

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="space-y-4">
          <h1 className="text-xl font-bold text-[var(--color-text-primary)] text-center">
            {t(`projectBlocked.${reason}.title`)}
          </h1>
          <Alert variant={variant}>{t(`projectBlocked.${reason}.body`)}</Alert>
          {/* Contact us — the customer-facing CTA on every blocked state (sales/
              support reactivate, renew a trial, etc.). Placeholder toast for now;
              real contact details land later. A sysadmin manages the project in
              the platform portal instead, so it's hidden for them. */}
          {!isSysadmin && (
            <div className="text-sm text-center">
              <Button variant="link" onClick={() => toast.info(t('tools.comingSoon', { ns: 'nav' }))}>
                {t('projectBlocked.contactUs')}
              </Button>
            </div>
          )}
          <div className="text-sm text-center">
            {isSysadmin ? (
              <Link to="/platform/projects">{t('projectBlocked.goToPlatform')}</Link>
            ) : hasReachable ? (
              <Link to="/select-project">{t('projectBlocked.chooseProject')}</Link>
            ) : (
              <Button variant="link" tone="danger" onClick={() => logout.mutate()}>
                {t('account.signOut', { ns: 'nav' })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectBlockedPage;
